import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash, Plus, Copy, Check, UserCircle, EnvelopeSimple, Crown } from "@phosphor-icons/react";
import { updateDistribution } from "@/lib/api";
import { toast } from "sonner";
import TransferOwnershipDialog from "@/components/TransferOwnershipDialog";

export default function ShareDialog({ open, onOpenChange, project, email, role, onUpdated }) {
  const isAdmin = role === "admin";
  const [entries, setEntries] = useState(() =>
    (project.distribution_list || []).map((e) => ({ ...e }))
  );
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("view");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const addEntry = () => {
    const em = newEmail.trim().toLowerCase();
    if (!em) return;
    if (entries.some((e) => e.email === em)) {
      toast.error("Email already in list");
      return;
    }
    setEntries([...entries, { email: em, role: newRole }]);
    setNewEmail("");
    setNewRole("view");
  };

  const removeEntry = (em) => {
    if (em === project.admin_email) {
      toast.error("Cannot remove the project admin");
      return;
    }
    setEntries(entries.filter((e) => e.email !== em));
  };

  const changeRole = (em, newR) => {
    if (em === project.admin_email) return;
    setEntries(entries.map((e) => (e.email === em ? { ...e, role: newR } : e)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateDistribution(project.id, entries);
      onUpdated(res.distribution_list);
      toast.success("Distribution list updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = `${window.location.origin}/project/${project.id}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Share Project</DialogTitle>
          <DialogDescription className="text-slate-500">
            Control who can access this project. Access is verified by email on open.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Link */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Shareable Link</Label>
            <div className="flex gap-2 mt-1.5">
              <Input readOnly value={shareUrl} className="h-10 font-mono-ibm text-xs rounded-md border-slate-300" data-testid="share-link-input" />
              <Button onClick={copyLink} variant="outline" className="h-10 rounded-md border-slate-300" data-testid="share-copy-btn">
                {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                <span className="ml-2">Copy</span>
              </Button>
            </div>
          </div>

          {/* Distribution list */}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Distribution List</Label>
            <div className="mt-2 border border-slate-300 rounded-md bg-white divide-y divide-slate-200 max-h-[280px] overflow-y-auto custom-scrollbar">
              {entries.map((e) => (
                <div key={e.email} className="flex items-center justify-between px-3 py-2.5 gap-3" data-testid={`dist-row-${e.email}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                      <UserCircle size={18} weight="fill" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-950 truncate">{e.email}</div>
                      {e.email === project.admin_email && (
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-ibm">Project Admin</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isAdmin && e.email !== project.admin_email ? (
                      <Select value={e.role} onValueChange={(v) => changeRole(e.email, v)}>
                        <SelectTrigger className="h-8 w-[100px] text-xs rounded-md border-slate-300" data-testid={`role-select-${e.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-md">
                          <SelectItem value="view">View</SelectItem>
                          <SelectItem value="edit">Edit</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-[11px] font-medium uppercase tracking-widest font-mono-ibm px-2 py-1 bg-slate-950 text-white rounded-md">
                        {e.role}
                      </span>
                    )}
                    {isAdmin && e.email !== project.admin_email && (
                      <button
                        onClick={() => removeEntry(e.email)}
                        className="h-8 w-8 rounded-md border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 text-slate-500 flex items-center justify-center"
                        data-testid={`remove-${e.email}`}
                      >
                        <Trash size={12} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add new */}
          {isAdmin && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">Add to Distribution List</Label>
              <div className="flex gap-2 mt-2">
                <div className="relative flex-1">
                  <EnvelopeSimple size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addEntry(); }}
                    placeholder="collaborator@company.com"
                    className="h-10 rounded-md border-slate-300 pl-9"
                    data-testid="new-email-input"
                  />
                </div>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="h-10 w-[110px] rounded-md border-slate-300" data-testid="new-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="view">View</SelectItem>
                    <SelectItem value="edit">Edit</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={addEntry}
                  className="h-10 rounded-md bg-slate-950 hover:bg-slate-800 text-white"
                  data-testid="add-email-btn"
                >
                  <Plus size={14} weight="bold" />
                  <span className="ml-1">Add</span>
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                Default access is <span className="font-semibold">View</span>. Only the admin can modify this list.
              </p>
            </div>
          )}

          {/* Transfer Ownership — admin only, and only when caller is the project owner */}
          {isAdmin && (
            <div className="border border-amber-200 bg-amber-50/60 rounded-md p-3 flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                <Crown size={16} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-950">Transfer ownership</div>
                <p className="text-[12px] text-slate-600 leading-relaxed mt-0.5">
                  Hand admin rights — and the billing seat — to another teammate. They'll get
                  an email link to accept.
                </p>
              </div>
              <Button
                onClick={() => setTransferOpen(true)}
                variant="outline"
                size="sm"
                className="h-9 rounded-md border-amber-300 text-amber-900 hover:bg-amber-100 shrink-0"
                data-testid="open-transfer-dialog-btn"
              >
                Transfer…
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">
            Cancel
          </Button>
          {isAdmin && (
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="save-distribution-btn"
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <TransferOwnershipDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        project={project}
      />
    </Dialog>
  );
}
